import availableFonts from "@builder/fonts/availableFonts";
import {
    deleteNodeHelper,
    duplicateListOfNodes,
    getAllDescendantsOfNode,
    getBaseLayer,
    getLayer,
    getNodeWithChildrenRecursive,
    getRootNode,
    isBaseLayerRootNode,
    isValidParent,
    leaveRootNodeMergeStyles,
    pasteNodes,
    updateNodePropertyOnLayer,
    findInnerIndex
} from "@builder/store/slices/BuilderSlice/builderHelpers";
import { BuilderState } from "@builder/store/slices/BuilderSlice/types";
import { convertJSXtoNodes } from "@builder/utils/JSXtoBuilder";
import { ActionBuilder, BaseLayer, BaseLayerTypes, BuildNode, BuildNodeQuery, DataType, Font } from "@common/build/types";
import { PayloadAction } from "@reduxjs/toolkit";
import { v4 } from "uuid";
import { containers } from "../builderMap";

export const clearBorderStyles = (state: BuilderState) => {
    if (state.selectedNode) {
        const baseLayer = getBaseLayer(state);
        const selectedNode = baseLayer.components[state.selectedNode];
        if (selectedNode?.attr?.style) {
            selectedNode.attr.style = Object.fromEntries(
                Object.entries(selectedNode.attr.style).filter(
                    ([key]) => !["borderRadius", "borderWidth", "borderStyle", "borderColor"].includes(key)
                )
            );
        }
    }
};

export const copyNode = (state: BuilderState, action: PayloadAction<string | undefined>) => {
    const baseLayer = getBaseLayer(state);
    const nodeToCopyId = action?.payload || state.selectedNode;
    if (baseLayer && nodeToCopyId) {
        const selectedNode = baseLayer.components[nodeToCopyId];
        if (selectedNode && selectedNode.parent) {
            const newNodes = getNodeWithChildrenRecursive(baseLayer, selectedNode, selectedNode.parent);
            const cloneOfNodes = duplicateListOfNodes(newNodes, selectedNode.parent);
            const parentComponent = baseLayer.components[selectedNode.parent];
            const originalIndex = parentComponent.conditionalVariations
                ? findInnerIndex(parentComponent.conditionalVariations, selectedNode.id)
                : parentComponent.children?.indexOf(selectedNode.id);

            if (originalIndex !== undefined && originalIndex !== -1 && (parentComponent.children || parentComponent.conditionalVariations)) {
                if (parentComponent.conditionalVariations) {
                    const cv = parentComponent.conditionalVariations.find(cv => {
                        return cv.children?.includes(selectedNode.id);
                    });
                    if (cv) {
                        cv.children = [
                            ...(cv.children?.slice(0, originalIndex + 1) || []),
                            cloneOfNodes[0].id,
                            ...(cv.children?.slice(originalIndex + 1) || [])
                        ];
                    }
                } else {
                    parentComponent.children = [
                        ...(parentComponent.children?.slice(0, originalIndex + 1) || []),
                        cloneOfNodes[0].id,
                        ...(parentComponent.children?.slice(originalIndex + 1) || [])
                    ];
                }
            }

            cloneOfNodes.forEach(node => (baseLayer.components[node.id] = node));
        }
    }
};

export const copyTemplateNode = (state: BuilderState, action: PayloadAction<{ layer: BaseLayer; node: BuildNode; action?: ActionBuilder }>) => {
    const baseLayer = getBaseLayer(state);
    if (baseLayer) {
        const templateLayer = action.payload.layer;
        const selectedNode = action.payload.node;
        const parentId = state.selectedNode ? baseLayer.components[state.selectedNode].id || baseLayer.root : baseLayer.root;
        const newNodes = getNodeWithChildrenRecursive(templateLayer, selectedNode, parentId);
        const parentComponent = baseLayer.components[parentId];

        //check for missing fonts:
        for (const node of newNodes) {
            const fontFamilyName = node?.attr?.style?.fontFamily;
            if (!state?.build?.extraAttributes?.fonts?.find(font => font.name === fontFamilyName)) {
                const newFont = availableFonts.find(font => font.name === fontFamilyName);
                if (newFont && newFont.name !== "inherit") {
                    if (!state.build.extraAttributes) {
                        state.build.extraAttributes = {};
                    }
                    if (!state.build.extraAttributes.fonts) {
                        state.build.extraAttributes.fonts = [];
                    }
                    state.build.extraAttributes.fonts.push(newFont);
                }
            }
        }

        if (parentComponent.children) {
            parentComponent.children.push(newNodes[0].id);
        } else {
            parentComponent.children = [newNodes[0].id];
        }

        if (action.payload.action) {
            if (state.build.actionBuilder) {
                state.build.actionBuilder[action.payload.action.id] = action.payload.action;
            } else {
                state.build.actionBuilder = {
                    [action.payload.action.id]: action.payload.action
                };
            }
        }

        newNodes.forEach(node => (baseLayer.components[node.id] = node));

        Object.entries(baseLayer.components).forEach(([key, value]) => {
            if (value.name === "templateDiv") {
                baseLayer.components[key].name = "div";
            }
        });
    }
};
export const pasteNodeFromJSON = (state: BuilderState, action: PayloadAction<string>) => {
    const baseLayer = getBaseLayer(state);
    const rootNode = getRootNode(baseLayer);
    let targetParent = rootNode;
    const selectedId = state.selectedNode;

    if (selectedId) {
        const selectedNode = baseLayer.components[selectedId];
        if (containers.includes(selectedNode.name)) {
            targetParent = selectedNode;
        } else if (selectedNode.parent) {
            targetParent = baseLayer.components[selectedNode.parent];
        }
    }

    const nodesToPaste = JSON.parse(action.payload);
    pasteNodes({ state, baseLayer, targetParent, nodes: nodesToPaste });
};

export const mergeJSXIntoNode = (state: BuilderState, action: PayloadAction<{ jsx: string; targetLayerId: string; targetElement?: string }>) => {
    const { jsx: JSX, targetLayerId, targetElement: targetId } = action.payload;
    const baseLayer = getLayer(state, targetLayerId);
    if (!baseLayer) {
        console.error("shit no base layer found in mergeJSXIntoNode");
        return;
    }

    const targetElement = targetId || baseLayer.root;
    const targetElementNode = baseLayer.components[targetElement];

    if (!targetElementNode.parent) {
        // Target element IS the root – merge children and styles
        const descendants = getAllDescendantsOfNode(targetElementNode, Object.values(baseLayer.components));
        const { rootNode, allNodes } = convertJSXtoNodes(JSX, targetElement, { fromNodes: descendants });
        if (!rootNode || allNodes.length === 0) return;

        for (const child of targetElementNode.children || []) {
            deleteNodeHelper(baseLayer, child);
        }

        if (!isBaseLayerRootNode(rootNode)) {
            targetElementNode.children = [rootNode.id];
            carefullyAddNodes(allNodes, baseLayer.components);
            return;
        }

        leaveRootNodeMergeStyles(rootNode, targetElementNode);
        targetElementNode.children = rootNode.children || [];

        allNodes.forEach(node => {
            if (node.parent === rootNode.id) {
                node.parent = targetElementNode.id;
            }
        });

        carefullyAddNodes(
            allNodes.filter(n => n.id !== rootNode.id),
            baseLayer.components
        );
    } else {
        // Target element is NOT the root - replace it
        const cvMode = state.conditionalVariationMode;
        const targetParentNode = baseLayer.components[targetElementNode.parent];
        const descendants = getAllDescendantsOfNode(targetParentNode, Object.values(baseLayer.components));

        const { rootNode, allNodes } = convertJSXtoNodes(JSX, targetElementNode.parent, { fromNodes: [...descendants, targetElementNode] });
        if (!rootNode || allNodes.length === 0) return;
        if (!isValidParent(baseLayer, rootNode, targetParentNode.id, undefined, !!cvMode?.id)) {
            console.warn("Invalid parent for root node", rootNode.name);
            return;
        }

        const originalRootId = rootNode.id;
        const originalIndex = targetParentNode.children?.indexOf(targetElementNode.id) ?? -1;

        deleteNodeHelper(baseLayer, targetElementNode.id);

        if (targetParentNode.children) {
            if (originalIndex > -1) {
                targetParentNode.children.splice(originalIndex, 0, rootNode.id);
            } else {
                targetParentNode.children.push(rootNode.id);
            }
        } else {
            targetParentNode.children = [rootNode.id];
        }

        carefullyAddNodes(allNodes, baseLayer.components);

        if (rootNode.id !== originalRootId && targetParentNode.children) {
            const idx = targetParentNode.children.indexOf(originalRootId);
            if (idx !== -1) targetParentNode.children[idx] = rootNode.id;
        }
    }
};

const carefullyAddNodes = (nodes: BuildNode[], components: Record<string, BuildNode>) => {
    const remap = new Map<string, string>();
    const usedIds = new Set<string>(Object.keys(components));

    nodes.forEach(node => {
        if (usedIds.has(node.id) || remap.has(node.id)) {
            let uid = v4();
            while (usedIds.has(uid) || remap.has(uid)) uid = v4();
            remap.set(node.id, uid);
            node.id = uid;
        }
        usedIds.add(node.id);
    });

    nodes.forEach(node => {
        if (node.parent && remap.has(node.parent)) node.parent = remap.get(node.parent)!;
        if (node.children && node.children.length) node.children = node.children.map(c => remap.get(c) || c);
    });

    console.log("carefully adding nodes:");
    console.log(nodes);
    nodes.forEach(node => {
        components[node.id] = node;
    });
};

export const updateNodeProperty = (
    state: BuilderState,
    action: PayloadAction<{ propertyPath: (string | number)[]; value: any | { [key: string]: any }; remove?: boolean }>
) => {
    const baseLayer = getBaseLayer(state);
    if (baseLayer && state.selectedNode) {
        const { propertyPath, value, remove } = action.payload;
        updateNodePropertyOnLayer(baseLayer, state.selectedNode, propertyPath, value, remove);
    }
    return state;
};

export const updateNodePropertyByNodeIdAndLayer = (
    state: BuilderState,
    action: PayloadAction<{
        layerType: "pages" | "popups" | "cfds";
        layerIndex: number;
        nodeId: string;
        propertyPath: (string | number)[];
        value: any;
    }>
) => {
    const { layerType, layerIndex, nodeId, propertyPath, value } = action.payload;
    const baseLayer = state.build[layerType][layerIndex];
    if (baseLayer) {
        updateNodePropertyOnLayer(baseLayer, nodeId, propertyPath, value);
    }
    return state;
};

export const addFontToAttributes = (state: BuilderState, action: PayloadAction<{ font: Font; asRoot?: boolean }>) => {
    const { font, asRoot } = action.payload;
    if (font.name === "inherit") return;
    state.build.extraAttributes = state.build.extraAttributes || {};
    state.build.extraAttributes.fonts = state.build.extraAttributes.fonts || [];

    state.build.extraAttributes.fonts = state.build.extraAttributes.fonts.filter(f => f.name !== font.name);

    if (asRoot) {
        state.build.extraAttributes.fonts.unshift(font);
    } else {
        state.build.extraAttributes.fonts.push(font);
    }
};

export const setFontRoots = (state: BuilderState, action: PayloadAction<Font>) => {
    for (const thing of [...state.build.pages, ...state.build.popups, ...state.build.cfds]) {
        const baseLayer = thing.components[thing.root];

        if (!baseLayer.attr) {
            baseLayer.attr = {};
        }
        if (!baseLayer.attr.style) {
            baseLayer.attr.style = {};
        }
        baseLayer.attr.style.fontFamily = action.payload.name;
    }
};
export const updateNodeQuery = (state: BuilderState, action: PayloadAction<BuildNodeQuery<DataType>>) => {
    if (state.selectedNode) {
        const baseLayer = getBaseLayer(state);
        if (baseLayer) {
            const selectedNode = baseLayer.components[state.selectedNode];
            if (selectedNode) {
                selectedNode.query = action.payload;
            }
        }
    }
};

export type filterNodesFn = (allNodes: BuildNode[]) => BuildNode[];

export const batchEditNodes = (
    state: BuilderState,
    action: PayloadAction<
        Array<{
            filter: filterNodesFn;
            update: { propertyPath: (string | number)[]; value: any | { [key: string]: any }; remove?: boolean };
            options?: { limitToLayer?: string };
        }>
    >
) => {
    const edits = action.payload;

    if (!edits || edits.length === 0) return state;

    const applySingleEdit = (edit: {
        filter: filterNodesFn;
        update: { propertyPath: (string | number)[]; value: any | { [key: string]: any }; remove?: boolean };
        options?: { limitToLayer?: string };
    }) => {
        const { filter, update, options } = edit;

        const targetLayers: BaseLayer[] = [];
        if (options?.limitToLayer) {
            const layer = getLayer(state, options.limitToLayer);
            if (layer) targetLayers.push(layer);
        } else {
            for (const layerType of Object.values(BaseLayerTypes)) {
                targetLayers.push(...state.build[layerType]);
            }
        }

        if (targetLayers.length === 0) return;

        const nodeIdToLayer = new Map<string, BaseLayer>();
        const allNodes: BuildNode[] = [];
        for (const layer of targetLayers) {
            const nodes = Object.values(layer.components);
            for (const n of nodes) {
                allNodes.push(n);
                nodeIdToLayer.set(n.id, layer);
            }
        }

        const selected = filter ? filter(allNodes) : [];
        if (!selected || selected.length === 0) return;

        const uniqueById = new Map<string, BuildNode>();
        for (const n of selected) uniqueById.set(n.id, n);

        for (const [nodeId] of uniqueById) {
            const layer = nodeIdToLayer.get(nodeId);
            if (!layer) continue;
            if (!layer.components[nodeId]) continue;

            updateNodePropertyOnLayer(layer, nodeId, update.propertyPath, update.value, update.remove);
        }
    };

    // Apply edits sequentially
    for (const edit of edits) {
        applySingleEdit(edit);
    }

    return state;
};
